import dayjs, { Dayjs } from "dayjs";
import { dayjsExt } from "./dayjs";

/**
 * Safe wrapper around dayjsExt().tz() that falls back to browser-local
 * dayjs if the timezone is not supported by the runtime.
 */
const safeTz = (input: string | Date | Dayjs, timeZone: string): Dayjs => {
  try {
    return dayjsExt(input).tz(timeZone);
  } catch {
    return dayjs(input);
  }
};

/**
 * Safe wrapper for dayjsExt.tz(dateString, timezone) — the "parse in timezone" form.
 */
const safeTzParse = (dateString: string, timeZone: string): Dayjs => {
  try {
    return dayjsExt.tz(dateString, timeZone);
  } catch {
    return dayjs(dateString);
  }
};

/**
 * Convert a UTC ISO string to a dayjs that shows venue-local wall-clock time
 * in the browser's pickers. E.g. 4PM Bucharest will show as 4PM in the picker
 * regardless of the user's browser timezone.
 */
export const utcToVenueLocal = (isoString: string | undefined | null, timeZone?: string): Dayjs | null => {
  if (!isoString) return null;
  if (!timeZone) return dayjs(isoString);
  const venueTime = safeTz(isoString, timeZone);
  return dayjs()
    .year(venueTime.year())
    .month(venueTime.month())
    .date(venueTime.date())
    .hour(venueTime.hour())
    .minute(venueTime.minute())
    .second(venueTime.second())
    .millisecond(0);
};

/**
 * Convert a dayjs from browser-local pickers to UTC ISO string,
 * interpreting the wall-clock values as venue-local time.
 * E.g. user picks 4PM → interpreted as 4PM in the venue timezone → correct UTC.
 */
export const venueLocalToUTC = (date: Dayjs | null, timeZone?: string): string | undefined => {
  if (!date) return undefined;
  if (!timeZone) return date.toJSON();
  const formatted = date.format('YYYY-MM-DDTHH:mm:ss');
  return safeTzParse(formatted, timeZone).toJSON();
};

export { safeTz, safeTzParse };

export const replaceDate = (original: Dayjs | null, newDate: Dayjs | null): Dayjs | null => {
  if (!original) {
    return original;
  }

  if (!newDate) {
    throw "Must provide a valid newDate";
  }

  const dayJsDate = dayjs(newDate);

  return original.set('year', dayJsDate.year()).set('month', dayJsDate.month()).set('date', dayJsDate.date());
};

/**
 * Mirrors Session.GetTicketingStartTime(): the engine starts sending ticket offers this far
 * before the arrival time — the session's own override when it has one, otherwise the show's
 * default. This is NOT the ticket launch date, which is only when the session goes live for
 * applications.
 *
 * Both offsets arrive either as a TimeSpan ("20.00:00:00") or as the plain day count the
 * session form posts ("14"); timeSpanToDays reads both.
 */
export const getTicketingStartTime = (
  arrivalTime: string | Dayjs | null | undefined,
  overrideOffset?: string | null,
  showDefaultOffset?: string | null
): Dayjs | null => {
  if (!arrivalTime) {
    return null;
  }

  const arrival = dayjs(arrivalTime);
  // A "0" override means "no override" to the backend, so it falls through to the show.
  const offset = overrideOffset && Number(timeSpanToDays(overrideOffset)) > 0
    ? overrideOffset
    : showDefaultOffset;
  const days = offset ? Number(timeSpanToDays(offset)) : NaN;

  return arrival.isValid() && Number.isFinite(days) ? arrival.subtract(days, "day") : null;
};

export const timeSpanToDays = (defaultTicketingStartTimeOffset: string): string => {
  const timeSpanParts = defaultTicketingStartTimeOffset.split(":");
  const days = parseInt(timeSpanParts[0], 10);

  return `${days}`;
}

export const calculateAge = (birthDate: Date) => {
  var today = new Date();
  var age = today.getUTCFullYear() - birthDate.getUTCFullYear();
  var m = today.getUTCMonth() - birthDate.getUTCMonth();
  if (m < 0 || (m === 0 && today.getUTCDate() < birthDate.getUTCDate())) {
    age--;
  }
  return age;
}

export const getMinimumDate = (present: Date) => {
  return new Date(Date.UTC(present.getFullYear() - 120, 1, 1));
}

export const minimumAge = 18;

export const validateBirthDate = (year: number | undefined, month: number | undefined, day: number | undefined) => {
  const present = new Date();
  const minimumDate = getMinimumDate(present);

  if (!year || !month || !day) {
    return false
  }

  const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));

  return (
    date < present &&
    date > minimumDate &&
    calculateAge(date) >= minimumAge &&
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

export const formatSessionDate = (showStartTime?: string | null, timeZone?: string) => {
  if (!showStartTime) return "";

  const tz = timeZone ?? "Australia/Sydney";
  const local = safeTz(showStartTime, tz);

  const hour = local.hour();

  let timePeriod;
  if (hour >= 5 && hour < 12) {
    timePeriod = "Morning";
  } else if (hour >= 12 && hour < 17) {
    timePeriod = "Afternoon";
  } else {
    timePeriod = "Evening";
  }

  return `${local.format("ddd")}, ${local.date()} ${local.format("MMM")}: ${timePeriod}`;
}

export const parseTimeString = (timeStr: string, date: Date | Dayjs = new Date(), timeFormat: string = "hhmmA"): Date => {
  return dayjs(`${dayjs(date).format('YYYY-MM-DD')} ${timeStr}`, `YYYY-MM-DD ${timeFormat}`).toDate()
}

export const minutesToMilliseconds = ( value: number) => {
  return value * 60 * 1000
}